/** A caret's box in viewport coordinates. */
export interface ICaretRect {
  top: number;
  left: number;
  height: number;
}

export interface IPopupSize {
  width: number;
  height: number;
}

export interface IViewportSize {
  width: number;
  height: number;
}

export type CaretPopupSide = "below" | "above";

/**
 * Where the popup's anchor goes, in viewport coordinates. `below`: `top` is the caret line's
 * bottom and the popup hangs from it; `above`: `top` is the caret line's top and the popup stands
 * on it. `left` is already clamped so the popup stays inside the viewport.
 */
export interface ICaretPopupPlacement {
  top: number;
  left: number;
  side: CaretPopupSide;
}

// Everything that decides where a line wraps and how tall it is. The mirror takes the textarea's
// resolved values, so the text inside it breaks exactly where the textarea's does.
const MIRRORED_PROPERTIES: readonly string[] = [
  "direction",
  "box-sizing",
  "width",
  "border-top-width",
  "border-right-width",
  "border-bottom-width",
  "border-left-width",
  "border-style",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "font-style",
  "font-variant",
  "font-weight",
  "font-stretch",
  "font-size",
  "font-size-adjust",
  "font-family",
  "font-feature-settings",
  "font-kerning",
  "line-height",
  "text-align",
  "text-transform",
  "text-indent",
  "letter-spacing",
  "word-spacing",
  "word-break",
  "tab-size"
];

const toPixels = (value: string): number => {
  const parsed: number = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * The caret's box at `index` in `textarea`, in viewport coordinates, measured with a mirror: a
 * hidden div that copies the textarea's box and typography, holds the text before `index`, and
 * marks the text from `index` on with a span — the span's first line box is where the caret is.
 * The rest of the text goes in the span (not a single marker glyph) so a word the caret sits in
 * wraps in the mirror exactly as it wraps in the field.
 *
 * The mirror lives for the length of this call only; nothing is left in the document.
 */
export const measureCaret = (textarea: HTMLTextAreaElement, index: number): ICaretRect => {
  const ownerDocument: Document = textarea.ownerDocument;
  const view: Window = ownerDocument.defaultView ?? window;
  const computed: CSSStyleDeclaration = view.getComputedStyle(textarea);

  const mirror: HTMLDivElement = ownerDocument.createElement("div");
  for (const property of MIRRORED_PROPERTIES) {
    mirror.style.setProperty(property, computed.getPropertyValue(property));
  }
  mirror.style.position = "absolute";
  mirror.style.top = "0";
  mirror.style.left = "0";
  mirror.style.visibility = "hidden";
  mirror.style.pointerEvents = "none";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.overflowWrap = "break-word";
  // A textarea with a scrollbar has that much less room for text; the mirror reserves the same.
  mirror.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? "scroll" : "hidden";

  const value: string = textarea.value;
  mirror.textContent = value.slice(0, index);
  const marker: HTMLSpanElement = ownerDocument.createElement("span");
  // An empty span has no line box to measure; a stand-in glyph gives it one.
  marker.textContent = value.slice(index) || ".";
  mirror.appendChild(marker);

  ownerDocument.body.appendChild(mirror);
  try {
    const box: DOMRect = textarea.getBoundingClientRect();
    const lineHeight: number = toPixels(computed.lineHeight) || toPixels(computed.fontSize);
    // offsetTop/offsetLeft are measured from the mirror's padding edge, so its border is added back.
    return {
      top: box.top + toPixels(computed.borderTopWidth) + marker.offsetTop - textarea.scrollTop,
      left: box.left + toPixels(computed.borderLeftWidth) + marker.offsetLeft - textarea.scrollLeft,
      height: lineHeight
    };
  } finally {
    mirror.remove();
  }
};

/**
 * Below the caret line when the popup fits there; above it when it only fits there; otherwise on
 * whichever side has more room. `offset` is the gap between the line and the popup, and also the
 * margin kept from the viewport's left and right edges.
 */
export const placeCaretPopup = (
  caret: ICaretRect,
  popup: IPopupSize,
  viewport: IViewportSize,
  offset: number
): ICaretPopupPlacement => {
  const caretBottom: number = caret.top + caret.height;
  const roomBelow: number = viewport.height - caretBottom - offset;
  const roomAbove: number = caret.top - offset;
  const fitsBelow: boolean = popup.height <= roomBelow;
  const fitsAbove: boolean = popup.height <= roomAbove;

  let side: CaretPopupSide = "below";
  if (!fitsBelow && (fitsAbove || roomAbove > roomBelow)) {
    side = "above";
  }

  const rightmost: number = viewport.width - offset - popup.width;
  const left: number = Math.max(offset, Math.min(caret.left, rightmost));

  return { top: side === "below" ? caretBottom : caret.top, left, side };
};
